// src/App.jsx – clean compile, no extra loading header,
// routes render immediately, multiplayer guarded by SocketLayout
// NOW WITH LIVE STREAMING BANNER!
import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useUser, SignedIn } from '@clerk/clerk-react'; // Added SignedIn import here!
import socket from './socket';

import LandingPage from './LandingPage';
import Dashboard from './Dashboard';
import Game from './frozzlers';               // Local/Guest game
import FrozzlersOnline from './FrozzlersOnline'; // Multiplayer using original visuals
import Lobby from './Lobby';
import LiveNowBanner from './components/LiveNowBanner'; // New live streaming banner
import CreatorEditor from './CreatorEditor';

// Socket authentication layout for multiplayer-only routes
function SocketLayout() {
  const { user } = useUser();
  const [isSocketAuthenticated, setIsSocketAuthenticated] = useState(false);

  useEffect(() => {
    if (!user) return;

    const ensureConnected = () => {
      if (!socket.connected) socket.connect();
    };

    const onConnect = () => {
      socket.emit('authenticate',
        { userId: user.id, firstName: user.firstName },
        () => setIsSocketAuthenticated(true)
      );
    };

    ensureConnected();
    socket.on('connect', onConnect);

    if (socket.connected) onConnect();

    return () => {
      socket.off('connect', onConnect);
    };
  }, [user]);

  if (!isSocketAuthenticated) {
    // Only multiplayer routes use this layout, so it's safe to gate here
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>🔌 Connecting to Multiplayer…</h2>
          <p>Authenticating with game servers…</p>
        </div>
      </div>
    );
  }

  // Pass authenticated socket down to nested routes
  return <Outlet context={{ socket }} />;
}

// Guest game (no socket needed)
function GuestGame() {
  const location = useLocation();
  useEffect(() => {
    const gameConfig = sessionStorage.getItem('gameConfig');
    if (gameConfig) {
      try {
        JSON.parse(gameConfig);
      } catch {
        /* noop */
      }
    }
  }, []);
  return <Game />;
}

export default function App() {
  const navigate = useNavigate();
  const [streamLobbyCode, setStreamLobbyCode] = useState(null);

  // Global socket navigations (lobby created, game started)
  useEffect(() => {
    const handleLobbyCreated = (lobby) => {
      // Check if this is a stream lobby and store the code
      if (lobby.isStreamLobby) {
        setStreamLobbyCode(lobby.id);
        // Optionally store in localStorage for the banner to read
        localStorage.setItem('currentStreamLobby', lobby.id);
      }
      navigate(`/lobby/${lobby.id}`);
    };
    
    const handleGameStarted = (data) => navigate(`/multiplayer-game/${data.lobbyId}`);

    socket.on('lobbyCreated', handleLobbyCreated);
    socket.on('gameStarted', handleGameStarted);
    
    return () => {
      socket.off('lobbyCreated', handleLobbyCreated);
      socket.off('gameStarted', handleGameStarted);
    };
  }, [navigate]);

  // Clean up stream lobby on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem('currentStreamLobby');
    };
  }, []);

  return (
    <>
      {/* Live Now Banner - appears on ALL pages when streaming */}
      <LiveNowBanner streamLobbyCode={streamLobbyCode} />
      
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/game" element={<GuestGame />} />
        
        {/* Creator Editor - protected route (auth handled internally) */}
        <Route path="/creator-editor" element={<CreatorEditor />} />
        
        {/* Multiplayer routes require authenticated socket */}
        <Route element={<SocketLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/lobby/:lobbyId" element={<Lobby />} />
          <Route path="/multiplayer-game/:lobbyId" element={<FrozzlersOnline />} />
        </Route>
      </Routes>
    </>
  );
}
